import { motion } from 'framer-motion';
import { UtensilsCrossed, Users, CalendarHeart, Phone, ChefHat, Truck, MapPin, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { PHONE_NUMBER } from '../data/menu';

const highlights = [
  { icon: UtensilsCrossed, title: 'Sizzling Shawarma', desc: 'Perfectly seasoned & freshly sliced', image: 'https://images.unsplash.com/photo-1756362847925-71c792d6729c?w=600&auto=format&fit=crop&q=60' },
  { icon: ChefHat, title: 'Grilled Kebabs', desc: 'Tender, juicy & flame-grilled', image: 'https://images.unsplash.com/photo-1647511006918-ad4b720de16d?w=600&auto=format&fit=crop&q=60' },
  { icon: Users, title: 'Whole Lamb Over Rice', desc: 'A showstopping traditional centerpiece', image: 'https://images.unsplash.com/photo-1771285119318-b342c3ecc51c?w=600&auto=format&fit=crop&q=60' },
  { icon: CalendarHeart, title: 'Mediterranean Appetizers', desc: 'Fresh dips, salads & traditional starters', image: 'https://images.unsplash.com/photo-1732969331543-80e52902e0a2?w=600&auto=format&fit=crop&q=60' },
];

const serviceOptions = [
  {
    icon: MapPin,
    title: 'We Cover Your Event',
    desc: 'Host with us — we handle everything from food to presentation.',
  },
  {
    icon: Truck,
    title: 'We Come to You',
    desc: 'Professional on-site catering & delivery to any venue in DFW.',
  },
];

export function CateringEvents() {
  const { t } = useLanguage();

  return (
    <section
      id="catering"
      className="py-20 px-4 relative overflow-hidden bg-[#faf7f2]"
      data-testid="catering-section"
    >
      {/* Decorative background shapes */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-[#6B1C23]/[0.03] rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -right-20 w-[500px] h-[500px] bg-[#6B1C23]/[0.03] rounded-full blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-6"
        >
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            whileInView={{ scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="w-16 h-16 bg-[#6B1C23]/10 rounded-full flex items-center justify-center mx-auto mb-5"
          >
            <CalendarHeart className="w-8 h-8 text-[#6B1C23]" />
          </motion.div>
          <h2
            className="font-cormorant text-4xl sm:text-5xl font-bold text-[#2C2C2C] mb-4"
            data-testid="catering-title"
          >
            Make Your Next Event Unforgettable
          </h2>
          <p className="font-manrope text-base text-[#666] max-w-2xl mx-auto leading-relaxed">
            Planning a special event in Richardson or the greater Dallas&#8209;Fort&nbsp;Worth area?
            Let Salam Grill bring the rich, vibrant flavors of the Mediterranean straight to your guests.
          </p>
        </motion.div>

        {/* How We Serve You — Two Cards */}
        <div className="grid sm:grid-cols-2 gap-5 mb-14 max-w-3xl mx-auto">
          {serviceOptions.map((opt, i) => {
            const Icon = opt.icon;
            return (
              <motion.div
                key={opt.title}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                whileHover={{ y: -4, boxShadow: '0 12px 30px rgba(107,28,35,0.08)' }}
                className="bg-white rounded-xl p-6 border border-[#e8e0d4] flex items-start gap-4 transition-all"
                data-testid={`service-option-${i}`}
              >
                <div className="w-11 h-11 rounded-full bg-[#6B1C23]/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Icon className="w-5 h-5 text-[#6B1C23]" />
                </div>
                <div>
                  <h3 className="font-cormorant text-xl font-bold text-[#2C2C2C] mb-1">{opt.title}</h3>
                  <p className="font-manrope text-sm text-[#777] leading-relaxed">{opt.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Menu Highlights */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-14"
        >
          <h3 className="font-cormorant text-2xl sm:text-3xl font-bold text-[#2C2C2C] text-center mb-8">
            A Menu Crafted to Impress
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {highlights.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -5, boxShadow: '0 10px 28px rgba(107,28,35,0.12)' }}
                className="group bg-white rounded-xl overflow-hidden border border-[#e8e0d4] transition-all"
                data-testid={`catering-feature-${i}`}
              >
                <div className="relative h-40 overflow-hidden">
                  <motion.img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.08 }}
                    transition={{ duration: 0.5 }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                </div>
                <div className="p-4 text-center">
                  <h4 className="font-cormorant text-lg font-bold text-[#2C2C2C] mb-1">{item.title}</h4>
                  <p className="font-manrope text-sm text-[#777]">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Tailored + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="rounded-xl overflow-hidden relative"
          data-testid="catering-cta"
        >
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1555244162-803834f70033?w=1200&auto=format&fit=crop&q=60"
              alt="Catering spread"
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-[#6B1C23]/85" />
          </div>

          <div className="relative z-10 p-8 sm:p-12 text-center">
            <h3 className="font-cormorant text-3xl sm:text-4xl font-bold text-white mb-3">
              Tailored to Your Vision & Budget
            </h3>
            <p className="font-manrope text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
              Every event is unique — no rigid packages here. Call <span className="text-white font-semibold">Sam</span> for
              a free consultation and we'll design a custom menu that perfectly fits your occasion.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                asChild
                size="lg"
                className="bg-white text-[#6B1C23] hover:bg-gray-100 px-10 py-6 font-manrope font-semibold shadow-lg text-lg"
                data-testid="catering-call-btn"
              >
                <a href={`tel:${PHONE_NUMBER}`} className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Call Sam Now
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                className="bg-[#FF6B35] text-white hover:bg-[#E55A2B] px-10 py-6 font-manrope font-semibold shadow-lg text-lg"
                data-testid="catering-ezcater-btn"
              >
                <a href="https://www.ezcater.com/catering/pvt/salam-gril-3?fcv=1" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                  <ExternalLink className="w-5 h-5" />
                  Order Catering Online
                </a>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
